import { useCallback, useEffect, useMemo, useState } from "react";
import { getAddressAttributes } from "@shared/v2/HandwrittenCard";
import { getStandardizedAddressApi } from "../../../PersonDetail/shared/api";

/**
 * Hook that extracts addresses from a person and optionally fetches their verification status.
 * @param {Object} person - The person object containing included addresses
 * @param {string} slug - The person's slug for API calls
 * @param {Object} options - Configuration options
 * @param {boolean} options.includeVerifiedStatus - Whether to fetch address verification status (default: true)
 * @returns {Object} addresses, standardizedAddresses, standardizedAddressesHash, isLoading, enableFetch
 */
const useStandardizedAddresses = (person, slug, { includeVerifiedStatus = true } = {}) => {
  const [standardizedAddressesHash, setStandardizedAddressesHash] = useState({});
  const [isEnabled, setIsEnabled] = useState(includeVerifiedStatus);
  const [fetchedSlug, setFetchedSlug] = useState(null);

  useEffect(() => {
    if (!isEnabled || !slug) return undefined;

    let cancelled = false;

    (async () => {
      const { res, err } = await getStandardizedAddressApi(slug);

      if (cancelled) return;

      if (err) {
        // eslint-disable-next-line no-console
        console.log("Error fetching standardized addresses:", err);
      } else {
        setStandardizedAddressesHash(res.data?.standardizedAddresses || {});
      }

      setFetchedSlug(slug);
    })();

    return () => {
      cancelled = true;
    };
  }, [isEnabled, slug]);

  const addresses = useMemo(() => getAddressAttributes(person), [person]);
  const standardizedAddresses = useMemo(
    () =>
      addresses.map((address) => ({
        ...address,
        standardized: address.standardized || standardizedAddressesHash[address.id]?.standardization,
      })),
    [addresses, standardizedAddressesHash],
  );

  // isLoading is true when we should fetch but haven't completed yet
  const isLoading = isEnabled && slug && fetchedSlug !== slug;

  const enableFetch = useCallback(() => setIsEnabled(true), []);

  return { addresses, standardizedAddresses, standardizedAddressesHash, isLoading, enableFetch };
};

export default useStandardizedAddresses;
